import { Link } from "react-router-dom";
import { MapPin, Clock, Briefcase, Wallet, Accessibility, Landmark, CalendarClock, ExternalLink } from "lucide-react";
import { Badge, SampleBadge, SaveButton, btn } from "./kit";
import { label, fmtDate, money } from "../lib/api";
import { ImportSource } from "./ImportSource";

const Meta = ({ icon: Icon, children }) => children ? <span className="inline-flex items-center gap-1.5"><Icon className="h-4 w-4 shrink-0" aria-hidden="true" />{children}</span> : null;

export function JobCard({ job, saved, extra }) {
  return (
    <article className="card p-5 md:p-6 flex flex-col gap-3" data-testid={`job-card-${job.id}`} aria-labelledby={`job-${job.id}-title`}>
      <div className="flex flex-wrap gap-2">
        {job.is_sample && <SampleBadge />}
        {job.featured && <Badge>Featured</Badge>}
        {job.pwd_eligibility_label && <Badge tone="grey">{job.pwd_eligibility_label}</Badge>}
        {job.accessibility_info && <Badge tone="green" icon={Accessibility}>Accessibility info provided by employer</Badge>}
      </div>
      <h3 id={`job-${job.id}-title`} className="text-xl font-bold"><Link to={`/jobs/${job.id}`} className="hover:underline underline-offset-4">{job.title}</Link></h3>
      <p className="font-medium text-ink">{job.company_name}</p>
      <ImportSource item={job} />
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sub">
        <Meta icon={MapPin}>{[job.location, label(job.work_mode)].filter(Boolean).join(" · ")}</Meta>
        <Meta icon={Briefcase}>{[label(job.employment_type), job.experience_level && `${label(job.experience_level)} level`].filter(Boolean).join(" · ")}</Meta>
        <Meta icon={Wallet}>{job.salary_text || money(job.salary_min, job.salary_max, job.salary_currency, " / year")}</Meta>
        <Meta icon={Clock}>Posted {fmtDate(job.published_at || job.created_at)}</Meta>
      </div>
      {job.match_reason && <p className="text-sm text-green font-medium">{job.match_reason}</p>}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Link to={`/jobs/${job.id}`} className={btn("primary", "sm")} data-testid={`view-job-${job.id}`}>View details<span className="sr-only"> for {job.title}</span></Link>
        {saved !== undefined && <SaveButton kind="job" id={job.id} initial={saved} />}
        {extra}
      </div>
    </article>
  );
}

export function ProjectCard({ p, saved }) {
  return (
    <article className="card p-5 md:p-6 flex flex-col gap-3" data-testid={`project-card-${p.id}`}>
      <div className="flex flex-wrap gap-2">{p.is_sample && <SampleBadge />}{p.category && <Badge>{p.category}</Badge>}</div>
      <h3 className="text-xl font-bold"><Link to={`/freelancing/${p.id}`} className="hover:underline underline-offset-4">{p.title}</Link></h3>
      <p className="text-sub line-clamp-2">{p.overview}</p>
      <ImportSource item={p} />
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sub">
        <Meta icon={Wallet}>{money(p.budget_min, p.budget_max, p.currency, p.payment_type === "hourly" ? " / hour" : " fixed")}</Meta>
        <Meta icon={MapPin}>{p.remote ? "Remote" : p.location || "On-site"}</Meta>
        <Meta icon={Clock}>{p.duration}</Meta>
      </div>
      {p.skills?.length > 0 && <ul className="flex flex-wrap gap-2" aria-label="Skills">{p.skills.map((s) => <li key={s}><Badge tone="grey">{s}</Badge></li>)}</ul>}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Link to={`/freelancing/${p.id}`} className={btn("primary", "sm")} data-testid={`view-project-${p.id}`}>View project<span className="sr-only">: {p.title}</span></Link>
        {saved !== undefined && <SaveButton kind="project" id={p.id} initial={saved} />}
      </div>
    </article>
  );
}

export function GovCard({ g, extra }) {
  return (
    <article className="card p-5 md:p-6 flex flex-col gap-3" data-testid={`gov-card-${g.id}`}>
      <div className="flex flex-wrap gap-2">
        {g.is_sample && <SampleBadge />}
        <Badge>{label(g.kind === "job" ? "Government job" : g.kind === "scheme" ? "Scheme / benefit" : "Guide")}</Badge>
        {g.level && <Badge tone="grey">{label(g.level)}</Badge>}
        {g.pwd_eligibility_label && (
          <Badge tone="grey" testid={`gov-pwd-eligibility-${g.id}`}>
            {g.pwd_eligibility_label}
          </Badge>
        )}
      </div>
      <h3 className="text-xl font-bold"><Link to={`/government/${g.id}`} className="hover:underline underline-offset-4">{g.title}</Link></h3>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-sub">
        <Meta icon={Landmark}>{g.authority}</Meta>
        <Meta icon={MapPin}>{g.state || (g.level === "central" ? "All India" : "")}</Meta>
        <Meta icon={CalendarClock}>{g.deadline && `Deadline ${fmtDate(g.deadline)}`}</Meta>
      </div>
      <p className="text-sm text-sub">{g.last_verified ? `Last verified ${fmtDate(g.last_verified)}` : "Not yet verified against an official source"}{g.source_name && ` · Source: ${g.source_name}`}</p>
      {extra}
      <div className="mt-auto flex flex-wrap gap-2 pt-2">
        <Link to={`/government/${g.id}`} className={btn("primary", "sm")} data-testid={`view-gov-${g.id}`}>View details<span className="sr-only">: {g.title}</span></Link>
        {g.official_url && <a href={g.official_url} target="_blank" rel="noopener noreferrer" className={btn("secondary", "sm")}>Official source<ExternalLink className="h-4 w-4" aria-hidden="true" /><span className="sr-only">(opens in new tab)</span></a>}
      </div>
    </article>
  );
}
